import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { isAppKey, type AppKey } from '@qms360/shared';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { PageMessage } from '../AppHome.js';
import { ApiError } from '../../lib/api.js';
import {
  fetchAiActivity,
  fetchAiSettings,
  removeAiKey,
  setAiPrice,
  storeAiKey,
  updateAiSettings,
  type AiProviderStatus,
  type AiSettingsView,
} from '../../lib/ai-settings-api.js';
import { t, formatDate, formatDateTime, formatNumber } from '../../lib/i18n.js';

/**
 * AI Settings & Controls (spec §13.3) — per application.
 *
 * What it is careful to say, rather than leave to be discovered:
 *
 *  - **What cannot be changed.** Human-in-the-loop and the ten-second timeout are
 *    stated as fixed, with no control, because a switch for them would be a
 *    switch for the thing that may not be switched off (ADR-0008, spec §1.8).
 *  - **When data leaves Algihaz.** An external vendor is marked as such, with the
 *    open governance question, before anyone activates it.
 *  - **That a key is write-only.** Once entered it is never shown again — only
 *    its last four characters.
 */
export function AiSettings() {
  const params = useParams<{ app: string }>();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const app = params.app;
  const valid = Boolean(app && isAppKey(app));
  const settings = useQuery({ queryKey: ['ai-settings', app], queryFn: () => fetchAiSettings(app!), enabled: valid });

  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['ai-settings', app] });
  };
  const fail = (fallback: string) => (caught: unknown) =>
    setError(caught instanceof ApiError ? caught.message : fallback);

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => updateAiSettings(app!, body),
    onSuccess: refresh,
    onError: fail(t("The AI settings could not be saved.")),
  });

  if (!app || !isAppKey(app)) {
    return <PageMessage title={t("Unknown application")} body="That application does not exist." />;
  }

  const data = settings.data;

  return (
    <div className="min-h-full">
      <AppTopBar app={app as AppKey} />
      <Breadcrumbs app={app as AppKey} trail={['Settings', 'AI Settings & Controls']} />

      <main className="mx-auto max-w-5xl px-4 pb-16 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("AI Settings & Controls")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("For this application only. The other two applications have their own.")}
        </p>

        {error && (
          <p role="alert" className="mt-4 text-sm" style={{ color: 'var(--tone-rejected)' }}>
            {error}
          </p>
        )}
        {settings.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}
        {settings.error && (
          <section className="mt-6 rounded-md border border-border bg-tone-info-surface p-6" role="status">
            <h2 className="m-0 text-base font-semibold">{t("Not available to your role")}</h2>
            <p className="mb-0 mt-2 text-sm text-content-secondary">
              {t("Managing AI needs the Manage AI Settings permission in this application.")}
            </p>
          </section>
        )}

        {data && (
          <>
            <Fixed data={data} />
            <Activation data={data} busy={update.isPending} onSave={(body) => update.mutate(body)} />
            <Features data={data} busy={update.isPending} onSave={(body) => update.mutate(body)} />
            <Quota data={data} busy={update.isPending} onSave={(body) => update.mutate(body)} />
            <Keys data={data} onDone={refresh} onError={setError} />
            <Usage data={data} />
            <Activity app={app} />
          </>
        )}
      </main>
    </div>
  );
}

function Section({ heading, note, children }: { heading: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="mt-8 rounded-md border border-border bg-surface p-5">
      <h2 className="m-0 text-lg font-semibold">{heading}</h2>
      {note && <p className="mt-1 text-sm text-content-secondary">{note}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Fixed({ data }: { data: AiSettingsView }) {
  return (
    <Section heading={t("Always on")}>
      <ul className="m-0 list-disc space-y-1 ps-5 text-sm" data-testid="ai-fixed">
        <li>
          <strong>{t("A person decides.")}</strong>{' '}{t("AI only ever suggests. Nothing it writes reaches a record until someone accepts or edits it, and that choice is recorded. This cannot be switched off.")}
        </li>
        <li>
          <strong>{data.fixed.timeoutSeconds}{t("-second limit.")}</strong>{' '}{t("A slower answer is dropped and the form carries on by hand.")}
        </li>
      </ul>
    </Section>
  );
}

function Activation({
  data,
  busy,
  onSave,
}: {
  data: AiSettingsView;
  busy: boolean;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [choice, setChoice] = useState<string>(data.provider ?? '');
  const [model, setModel] = useState<string>(data.model ?? '');
  const chosen = data.providers.find((provider) => provider.key === choice);

  return (
    <Section
      heading={t("Provider")}
      note={t("Which AI service this application uses. None is switched on until someone chooses one here.")}
    >
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          onSave({ provider: choice || null, model: model.trim() || null });
        }}
      >
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Provider")}</span>
          <select
            value={choice}
            onChange={(event) => setChoice(event.target.value)}
            className="rounded border border-border px-2 py-1"
          >
            <option value="">{t("Off — every form is filled in by hand")}</option>
            {data.providers.map((provider) => (
              <option key={provider.key} value={provider.key} disabled={!provider.configured}>
                {providerName(provider)}
                {provider.configured ? '' : t(" — not set up on this server")}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Model (optional)")}</span>
          <input
            type="text"
            value={model}
            placeholder={chosen?.defaultModel ?? ''}
            onChange={(event) => setModel(event.target.value)}
            className="w-56 rounded border border-border px-2 py-1"
          />
        </label>
        <button type="submit" disabled={busy} className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white disabled:opacity-60">
          {t("Save")}
        </button>
      </form>

      {chosen?.external && (
        <p role="note" className="mb-0 mt-3 rounded bg-tone-pending-surface p-3 text-sm" data-testid="external-warning">
          <strong>{t("Data leaves Algihaz’s systems.")}</strong> {providerName(chosen)}{' '}{t("runs outside Algihaz. The figures used for each suggestion are sent to it. Algihaz has not yet confirmed which providers are permitted, so switching this on is a decision that is recorded under your name.")}
        </p>
      )}
      {chosen?.demonstrationOnly && (
        <p role="note" className="mb-0 mt-3 rounded bg-tone-info-surface p-3 text-sm">
          {t("The demonstration provider writes fixed text from the figures. It is for trying the screens, not for real records.")}
        </p>
      )}
    </Section>
  );
}

function providerName(provider: AiProviderStatus): string {
  const names: Record<string, string> = {
    anthropic: 'Claude (Anthropic)',
    gemini: 'Gemini (Google)',
    local: 'Local model (on Algihaz hardware)',
    stub: 'Demonstration (no AI)',
  };
  return names[provider.key] ?? provider.key;
}

function Features({
  data,
  busy,
  onSave,
}: {
  data: AiSettingsView;
  busy: boolean;
  onSave: (body: Record<string, unknown>) => void;
}) {
  if (data.features.length === 0) {
    return (
      <Section heading={t("Features")}>
        <p className="m-0 text-sm text-content-secondary">{t("This application has no AI features yet.")}</p>
      </Section>
    );
  }
  return (
    <Section heading={t("Features")} note={t("Each one on or off. A feature switched off is off for everyone, whatever the screen shows.")}>
      <ul className="m-0 list-none space-y-2 p-0">
        {data.features.map((feature) => (
          <li key={feature.key}>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={feature.enabled}
                disabled={busy}
                onChange={(event) => onSave({ featureToggles: { [feature.key]: event.target.checked } })}
              />
              {t(feature.label)}
            </label>
          </li>
        ))}
      </ul>
      {data.app.key === 'qaqc' && (
        <label className="mt-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={data.guardrails.sendProjectNames}
            disabled={busy}
            onChange={(event) => onSave({ guardrails: { sendProjectNames: event.target.checked } })}
          />
          {t("Send project names to the provider (otherwise only project codes)")}
        </label>
      )}
    </Section>
  );
}

function Quota({
  data,
  busy,
  onSave,
}: {
  data: AiSettingsView;
  busy: boolean;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [value, setValue] = useState(data.monthlyQuota === null ? '' : String(data.monthlyQuota));
  const parsed = value.trim() === '' ? null : Number(value);
  const valid = parsed === null || (Number.isInteger(parsed) && parsed >= 1);
  return (
    <Section
      heading={t("Monthly quota")}
      note={t("Suggestions this month: {0}{1}. When the quota is reached, forms carry on by hand until the 1st.", { 0: data.usedThisMonth, 1: data.monthlyQuota ? ` of ${data.monthlyQuota}` : '' })}
    >
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) onSave({ monthlyQuota: parsed });
        }}
      >
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Suggestions per month")}</span>
          <input
            type="number"
            min={1}
            placeholder={t("No limit")}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            className="w-32 rounded border border-border px-2 py-1"
          />
        </label>
        <button type="submit" disabled={!valid || busy} className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white disabled:opacity-60">
          {t("Save")}
        </button>
      </form>
    </Section>
  );
}

function Keys({ data, onDone, onError }: { data: AiSettingsView; onDone: () => void; onError: (message: string) => void }) {
  return (
    <Section
      heading={t("API keys and prices")}
      note={t("Shared by all three applications. A key is never shown again once saved — only its last four characters.")}
    >
      {!data.vaultAvailable && (
        <p className="mt-0 text-sm text-content-secondary">
          {t("Key storage is not set up on this server, so keys can only come from the server’s own configuration.")}
        </p>
      )}
      {!data.managesKeysAndPrices && (
        <p className="mt-0 text-sm text-content-secondary">
          {t("Changing keys and prices needs AI settings authority in all three applications.")}
        </p>
      )}
      <ul className="m-0 list-none space-y-4 p-0">
        {data.providers.map((provider) => (
          <li key={provider.key} className="rounded border border-border p-3 text-sm">
            <p className="m-0 font-medium">{providerName(provider)}</p>
            {provider.takesKey && (
              <KeyRow provider={provider} canChange={data.vaultAvailable && data.managesKeysAndPrices} onDone={onDone} onError={onError} />
            )}
            <PriceRow provider={provider} canChange={data.managesKeysAndPrices} onDone={onDone} onError={onError} />
          </li>
        ))}
      </ul>
    </Section>
  );
}

function KeyRow({
  provider,
  canChange,
  onDone,
  onError,
}: {
  provider: AiProviderStatus;
  canChange: boolean;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [secret, setSecret] = useState('');
  const save = useMutation({
    mutationFn: () => storeAiKey(provider.key, secret),
    onSuccess: () => {
      setSecret('');
      onDone();
    },
    onError: (caught) => onError(caught instanceof ApiError ? caught.message : t("The key could not be saved.")),
  });
  const remove = useMutation({
    mutationFn: () => removeAiKey(provider.key),
    onSuccess: onDone,
    onError: (caught) => onError(caught instanceof ApiError ? caught.message : t("The key could not be removed.")),
  });

  const stored = provider.storedKey;
  return (
    <div className="mt-2">
      <p className="m-0 text-content-secondary" data-testid={`key-status-${provider.key}`}>
        {stored?.set
          ? stored.unreadable
            ? t("A key is stored but this server can no longer read it — enter it again.")
            : t("Key ending {0}, saved {1}.", { 0: stored.hint, 1: stored.setAt ? formatDate(stored.setAt) : '' })
          : provider.keySource === 'environment'
            ? t("Using the key in the server’s configuration.")
            : t("No key.")}
      </p>
      {canChange && (
        <form
          className="mt-2 flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (secret.trim().length >= 8) save.mutate();
          }}
        >
          <input
            type="password"
            autoComplete="off"
            aria-label={t("{0} API key", { 0: providerName(provider) })}
            placeholder={stored?.set ? t("Replace the key") : t("Paste the API key")}
            value={secret}
            onChange={(event) => setSecret(event.target.value)}
            className="w-72 rounded border border-border px-2 py-1"
          />
          <button type="submit" disabled={save.isPending || secret.trim().length < 8} className="rounded border border-border px-2 py-1 disabled:opacity-60">
            {t("Save key")}
          </button>
          {stored?.set && (
            <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending} className="rounded border border-border px-2 py-1">
              {t("Remove")}
            </button>
          )}
        </form>
      )}
    </div>
  );
}

function PriceRow({
  provider,
  canChange,
  onDone,
  onError,
}: {
  provider: AiProviderStatus;
  canChange: boolean;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [input, setInput] = useState(provider.price ? String(provider.price.inputPerMillion) : '');
  const [output, setOutput] = useState(provider.price ? String(provider.price.outputPerMillion) : '');
  const [currency, setCurrency] = useState(provider.price?.currency ?? 'USD');
  const save = useMutation({
    mutationFn: () => setAiPrice(provider.key, { inputPerMillion: Number(input), outputPerMillion: Number(output), currency }),
    onSuccess: onDone,
    onError: (caught) => onError(caught instanceof ApiError ? caught.message : t("The price could not be saved.")),
  });
  const valid = input !== '' && output !== '' && Number(input) >= 0 && Number(output) >= 0 && /^[A-Z]{3}$/.test(currency);

  return (
    <div className="mt-2">
      <p className="m-0 text-content-secondary">
        {provider.price
          ? t("Price: {0} in / {1} out {2} per million tokens.", { 0: provider.price.inputPerMillion, 1: provider.price.outputPerMillion, 2: provider.price.currency })
          : t("No price — its calls are counted but not costed.")}
      </p>
      {canChange && (
        <form
          className="mt-2 flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <input aria-label={t("Input price per million tokens")} type="number" min={0} step="any" value={input} onChange={(event) => setInput(event.target.value)} className="w-24 rounded border border-border px-2 py-1" />
          <input aria-label={t("Output price per million tokens")} type="number" min={0} step="any" value={output} onChange={(event) => setOutput(event.target.value)} className="w-24 rounded border border-border px-2 py-1" />
          <input aria-label={t("Currency")} type="text" maxLength={3} value={currency} onChange={(event) => setCurrency(event.target.value.toUpperCase())} className="w-16 rounded border border-border px-2 py-1" />
          <button type="submit" disabled={!valid || save.isPending} className="rounded border border-border px-2 py-1 disabled:opacity-60">
            {t("Save price")}
          </button>
        </form>
      )}
    </div>
  );
}

function Usage({ data }: { data: AiSettingsView }) {
  return (
    <Section heading={t("Usage")} note={t("The last six months. Costs use the price in force when each suggestion was made.")}>
      {data.usage.length === 0 ? (
        <p className="m-0 text-sm text-content-secondary">{t("No AI suggestions yet.")}</p>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-content-secondary">
              {['Month', 'Feature', 'Provider', 'Calls', 'Accepted / edited / rejected', 'Timed out', 'Tokens in / out', 'Cost'].map((heading) => (
                <th key={heading} className="py-2 pe-3 text-start font-medium">
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.usage.map((line) => (
              <tr key={`${line.month}-${line.feature}-${line.provider}`} className="border-b border-border">
                <td className="py-2 pe-3">{line.month}</td>
                <td className="py-2 pe-3">{line.feature.replace(/_/g, ' ')}</td>
                <td className="py-2 pe-3">{line.provider}</td>
                <td className="py-2 pe-3">{line.calls}</td>
                <td className="py-2 pe-3">
                  {line.accepted} / {line.edited} / {line.rejected}
                </td>
                <td className="py-2 pe-3">{line.timedOut}</td>
                <td className="py-2 pe-3">
                  {formatNumber(line.inputTokens)} / {formatNumber(line.outputTokens)}
                </td>
                <td className="py-2 pe-3">
                  {Object.keys(line.cost).length === 0
                    ? '—'
                    : Object.entries(line.cost)
                        .map(([currency, amount]) => `${amount.toFixed(4)} ${currency}`)
                        .join(', ')}
                  {line.uncosted > 0 && Object.keys(line.cost).length > 0 && (
                    <span className="block text-xs text-content-secondary">{line.uncosted}{' '}{t("not costed")}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function Activity({ app }: { app: string }) {
  const [shown, setShown] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const activity = useInfiniteQuery({
    queryKey: ['ai-activity', app],
    queryFn: ({ pageParam }) => fetchAiActivity(app, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // Reading it is recorded, so it is fetched only when somebody asks.
    enabled: shown,
    refetchOnWindowFocus: false,
  });
  const entries = activity.data?.pages.flatMap((page) => page.entries) ?? [];

  return (
    <Section
      heading={t("AI activity")}
      note={t("Every suggestion: what was asked, what came back, and what the person did with it. Opening this list is recorded.")}
    >
      {!shown ? (
        <button type="button" onClick={() => setShown(true)} className="rounded border border-border px-3 py-1.5 text-sm">
          {t("Show AI activity")}
        </button>
      ) : entries.length === 0 && !activity.isLoading ? (
        <p className="m-0 text-sm text-content-secondary">{t("No AI suggestions yet.")}</p>
      ) : (
        <>
          <ul className="m-0 list-none space-y-2 p-0">
            {entries.map((entry) => (
              <li key={entry.id} className="rounded border border-border p-3 text-sm">
                <button type="button" onClick={() => setOpen(open === entry.id ? null : entry.id)} aria-expanded={open === entry.id} className="text-start">
                  <strong>{formatDateTime(entry.createdAt)}</strong> · {entry.feature.replace(/_/g, ' ')} ·{' '}
                  {entry.userName ?? '—'} · <span className="text-content-secondary">{entry.disposition.replace(/_/g, ' ')}</span>
                </button>
                {open === entry.id && (
                  <div className="mt-2 space-y-2 text-xs">
                    <p className="m-0 text-content-secondary">
                      {entry.provider} · {entry.model} · {entry.latencyMs ?? '—'}{' '}{t("ms · tokens")}{' '}{entry.inputTokens ?? '—'} / {entry.outputTokens ?? '—'}
                      {entry.estimatedCost ? ` · ${entry.estimatedCost}` : ''}
                    </p>
                    {entry.promptText && <Block label={t("Asked")} text={entry.promptText} />}
                    {entry.responseText && <Block label={t("Suggested")} text={entry.responseText} />}
                    {entry.finalText && <Block label={t("Kept (edited)")} text={entry.finalText} />}
                    {entry.errorMessage && <Block label={t("Error")} text={entry.errorMessage} />}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {activity.hasNextPage && (
            <button type="button" onClick={() => void activity.fetchNextPage()} className="mt-3 rounded border border-border px-3 py-1.5 text-sm">
              {t("Load older")}
            </button>
          )}
        </>
      )}
    </Section>
  );
}

function Block({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <p className="m-0 font-medium">{label}</p>
      <pre className="m-0 mt-1 whitespace-pre-wrap rounded bg-surface-sunken p-2">{text}</pre>
    </div>
  );
}
